/**
 * GridMask — a Set of (row,col) positions that define which cells exist in the grid.
 * Supports shape placement validation and bounding box computation.
 */

import { Cell, cellKey, Shape } from "../../../../framework/geometry";

const DIRS: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];

export class GridMask {
  private cells = new Set<string>();

  constructor(cells?: Iterable<Cell>) {
    if (cells) {
      for (const c of cells) this.cells.add(cellKey(c));
    }
  }

  static base(size: number = 4): GridMask {
    const mask = new GridMask();
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        mask.add({ row: r, col: c });
      }
    }
    return mask;
  }

  has(cell: Cell): boolean { return this.cells.has(cellKey(cell)); }
  add(cell: Cell): void { this.cells.add(cellKey(cell)); }
  remove(cell: Cell): void { this.cells.delete(cellKey(cell)); }
  get count(): number { return this.cells.size; }

  toArray(): Cell[] {
    return [...this.cells].map(k => {
      const [r, c] = k.split(",").map(Number);
      return { row: r, col: c };
    });
  }

  bounds(): { minRow: number; maxRow: number; minCol: number; maxCol: number } {
    let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
    for (const cell of this.toArray()) {
      if (cell.row < minR) minR = cell.row;
      if (cell.row > maxR) maxR = cell.row;
      if (cell.col < minC) minC = cell.col;
      if (cell.col > maxC) maxC = cell.col;
    }
    return { minRow: minR, maxRow: maxR, minCol: minC, maxCol: maxC };
  }

  get rows(): number { const b = this.bounds(); return b.maxRow - b.minRow + 1; }
  get cols(): number { const b = this.bounds(); return b.maxCol - b.minCol + 1; }

  /** Place a shape at anchor offset (dr, dc). */
  placeShape(shape: Shape, dr: number, dc: number): void {
    for (const c of shape.cells) {
      this.add({ row: c.row + dr, col: c.col + dc });
    }
  }

  /** Check if shape can be placed: no overlaps + at least one cell adjacent to existing. */
  canPlace(shape: Shape, dr: number, dc: number): boolean {
    const translated = shape.cells.map(c => ({ row: c.row + dr, col: c.col + dc }));
    for (const c of translated) {
      if (this.has(c)) return false;
    }
    let hasNeighbor = false;
    for (const c of translated) {
      for (const [ddr, ddc] of DIRS) {
        if (this.has({ row: c.row + ddr, col: c.col + ddc })) {
          hasNeighbor = true;
          break;
        }
      }
      if (hasNeighbor) break;
    }
    return hasNeighbor;
  }

  /** All valid anchor positions for a shape, capped to max bounding box. */
  validPlacements(shape: Shape, maxDim: number = 10): Cell[] {
    const b = this.bounds();
    const results: Cell[] = [];
    const pad = Math.max(shape.height, shape.width);
    for (let r = b.minRow - pad; r <= b.maxRow + pad; r++) {
      for (let c = b.minCol - pad; c <= b.maxCol + pad; c++) {
        if (!this.canPlace(shape, r, c)) continue;
        // Check resulting bounding box doesn't exceed max
        const shapeCells = shape.cells.map(sc => ({ row: sc.row + r, col: sc.col + c }));
        let mr = b.minRow, xr = b.maxRow, mc = b.minCol, xc = b.maxCol;
        for (const sc of shapeCells) {
          if (sc.row < mr) mr = sc.row;
          if (sc.row > xr) xr = sc.row;
          if (sc.col < mc) mc = sc.col;
          if (sc.col > xc) xc = sc.col;
        }
        if (xr - mr + 1 <= maxDim && xc - mc + 1 <= maxDim) {
          results.push({ row: r, col: c });
        }
      }
    }
    return results;
  }

  /** Add a full row at the top edge. */
  addRowTop(): void {
    const b = this.bounds();
    const newRow = b.minRow - 1;
    for (let c = b.minCol; c <= b.maxCol; c++) {
      this.add({ row: newRow, col: c });
    }
  }

  /** Add a full row at the bottom edge. */
  addRowBottom(): void {
    const b = this.bounds();
    const newRow = b.maxRow + 1;
    for (let c = b.minCol; c <= b.maxCol; c++) {
      this.add({ row: newRow, col: c });
    }
  }

  /** Add a full column at the left edge. */
  addColLeft(): void {
    const b = this.bounds();
    const newCol = b.minCol - 1;
    for (let r = b.minRow; r <= b.maxRow; r++) {
      this.add({ row: r, col: newCol });
    }
  }

  /** Add a full column at the right edge. */
  addColRight(): void {
    const b = this.bounds();
    const newCol = b.maxCol + 1;
    for (let r = b.minRow; r <= b.maxRow; r++) {
      this.add({ row: r, col: newCol });
    }
  }

  serialize(): [number, number][] {
    return this.toArray().map(c => [c.row, c.col]);
  }

  static deserialize(data: [number, number][]): GridMask {
    return new GridMask(data.map(([r, c]) => ({ row: r, col: c })));
  }
}
